import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "热门榜单", to: "/ranking" },
  { label: "新剧速递", to: "/ranking?tab=new" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const solid = scrolled || !isHome || menuOpen;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/ranking?q=${encodeURIComponent(value)}` : "/ranking");
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? "bg-background-100/95 backdrop-blur border-b border-background-200/70" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-20 items-center justify-between gap-4 px-4 md:px-6 max-w-page">
        <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="黑料剧场 首页">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-950">
            <i className="ri-movie-2-line text-xl" aria-hidden="true"></i>
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading text-lg font-bold text-foreground-950">黑料剧场</span>
            <span className="text-[11px] tracking-widest text-foreground-600 font-label">
              短剧榜单 · 同款推荐
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
          {NAV_ITEMS.map((item) => {
            const active = item.to === "/" ? location.pathname === "/" : location.pathname === item.to.split("?")[0] && !item.to.includes("tab=new");
            return (
              <Link
                key={item.label}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-label transition-colors cursor-pointer ${
                  active
                    ? "bg-background-200 text-foreground-950"
                    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-200/60"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form onSubmit={submitSearch} className="hidden md:flex items-center" role="search">
            <div className="flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100/80 px-3 py-2 focus-within:border-primary-400">
              <i className="ri-search-line text-foreground-600 text-sm" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜剧名 / 口味"
                aria-label="搜索短剧"
                className="w-32 xl:w-44 bg-transparent text-sm font-label text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
              />
            </div>
          </form>

          <Link
            to="/ranking"
            className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-950 transition-colors hover:bg-primary-400 cursor-pointer"
          >
            <i className="ri-fire-line" aria-hidden="true"></i>
            看榜单
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-md border border-background-300/70 text-foreground-950 cursor-pointer"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200/70 bg-background-100/95 backdrop-blur px-4 pb-6 pt-4">
          <form onSubmit={submitSearch} className="mb-4 flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-3 py-2.5" role="search">
            <i className="ri-search-line text-foreground-600 text-sm" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜剧名 / 口味"
              aria-label="搜索短剧"
              className="w-full bg-transparent text-sm font-label text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
            />
          </form>
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="rounded-md px-3 py-3 text-sm font-label text-foreground-800 transition-colors hover:bg-background-200 cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}